import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';

// Ensure you have a CSS file for this component or add styles here
// import "./CompanySettings.css"; 

const API_URL = process.env.REACT_APP_API_URL;

// A simple modal component wrapper for reusability
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="modal-close-btn">&times;</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const CompanySettings = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // State to manage which modal is open
  const [activeModal, setActiveModal] = useState(null); // e.g., 'name', 'address', 'gst'
  // State to hold form data for the active modal
  const [formData, setFormData] = useState({});
  // State for the two-step confirmation process
  const [showConfirmation, setShowConfirmation] = useState(false);

  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProfile = useCallback(async () => {
    if (!token) navigate("/login");
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/settings/company-profile`, { headers });
      setProfile(res.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch company profile. Please try again later.");
      if (err.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [headers, navigate, token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleOpenModal = (modalName, data) => {
    setActiveModal(modalName);
    setFormData(data);
    setShowConfirmation(false); // Reset confirmation on open
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setFormData({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    // If this is a major change and confirmation hasn't been shown, show it.
    if (['name', 'gst'].includes(activeModal) && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/settings/company-profile`, formData, { headers });
      await fetchProfile(); // Refresh data from server
      handleCloseModal();
    } catch (err) {
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <p style={{ textAlign: "center", padding: "2rem" }}>Loading Company Settings...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!profile) return null;

  const renderField = (label, value) => (
    <div className="field-row">
      <span className="field-label">{label}</span>
      <span className="field-value">{value || <i className="not-set">Not Set</i>}</span>
    </div>
  );

  return (
    <div className="settings-container">

      {/* --- Company Identity Card --- */}
      <div className="settings-card">
        <div className="card-header">
          <h4>Company Identity</h4>
          <button className="btn btn-outline" onClick={() => handleOpenModal('name', { name: profile.name })}>
            Edit Name
          </button>
        </div>
        <div className="card-body">
          {renderField("Company Name", profile.name)}
        </div>
      </div>

      {/* --- Contact & Address Card --- */}
      <div className="settings-card">
        <div className="card-header">
          <h4>Address & Contact</h4>
          <button className="btn btn-outline" onClick={() => handleOpenModal('address', { address: profile.address, phone: profile.phone })}>
            Edit Details
          </button>
        </div>
        <div className="card-body">
          {renderField("Address", profile.address)}
          {renderField("Phone", profile.phone)}
        </div>
      </div>
      
      {/* --- Tax & Legal Card --- */}
      <div className="settings-card">
        <div className="card-header">
          <h4>Tax & Legal Information</h4>
          <button className="btn btn-outline" onClick={() => handleOpenModal('gst', { gst_no: profile.gst_no, fssai_no: profile.fssai_no, arn_no: profile.arn_no })}>
            Edit Numbers
          </button>
        </div>
        <div className="card-body">
          {renderField("GST Number", profile.gst_no)}
          {renderField("FSSAI Number", profile.fssai_no)}
          {renderField("ARN Number", profile.arn_no)}
        </div>
      </div>

      {/* --- Bank Details Card --- */}
      <div className="settings-card">
        <div className="card-header">
          <h4>Bank Details</h4>
           <button className="btn btn-outline" onClick={() => handleOpenModal('bank', { bank_details: profile.bank_details })}>
            Edit Bank Info
          </button>
        </div>
        <div className="card-body">
           <div className="field-row">
            <span className="field-label">Bank Details</span>
            <div className="field-value" dangerouslySetInnerHTML={{ __html: profile.bank_details || '<i>Not Set</i>' }} />
          </div>
        </div>
      </div>

      {/* ====== MODALS ====== */}

      {/* --- Edit Name Modal --- */}
      <Modal isOpen={activeModal === 'name'} onClose={handleCloseModal} title="Edit Company Name">
        {!showConfirmation ? (
          <>
            <div className="form-group">
              <label htmlFor="name">Company Name</label>
              <input type="text" id="name" name="name" value={formData.name || ''} onChange={handleInputChange} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={handleCloseModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveChanges} disabled={isSaving}>
                Save Changes
              </button>
            </div>
          </>
        ) : (
          <div className="confirmation-dialog">
            <h4>Major Change Confirmation</h4>
            <p className="warning-text">
              <strong>Warning:</strong> Changing the company name will affect all future documents. This is a significant change.
            </p>
            <p>Please confirm you want to proceed.</p>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowConfirmation(false)}>Back to Edit</button>
              <button className="btn btn-danger" onClick={handleSaveChanges} disabled={isSaving}>
                {isSaving ? "Saving..." : "Confirm and Save"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* --- Edit Address & Contact Modal --- */}
      <Modal isOpen={activeModal === 'address'} onClose={handleCloseModal} title="Edit Address & Contact">
        <div className="form-group">
          <label htmlFor="address">Address</label>
          <textarea id="address" name="address" rows="3" value={formData.address || ''} onChange={handleInputChange}></textarea>
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone Number(s)</label>
          <input type="text" id="phone" name="phone" value={formData.phone || ''} onChange={handleInputChange} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={handleCloseModal}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveChanges} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </Modal>
      
       {/* --- Edit Tax & Legal Modal --- */}
      <Modal isOpen={activeModal === 'gst'} onClose={handleCloseModal} title="Edit Tax & Legal Information">
        {!showConfirmation ? (
          <>
            <div className="form-group">
              <label htmlFor="gst_no">GST Number</label>
              <input type="text" id="gst_no" name="gst_no" value={formData.gst_no || ''} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label htmlFor="fssai_no">FSSAI Number</label>
              <input type="text" id="fssai_no" name="fssai_no" value={formData.fssai_no || ''} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label htmlFor="arn_no">ARN Number</label>
              <input type="text" id="arn_no" name="arn_no" value={formData.arn_no || ''} onChange={handleInputChange} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={handleCloseModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveChanges} disabled={isSaving}>
                Save Changes
              </button>
            </div>
          </>
        ) : (
          <div className="confirmation-dialog">
            <h4>Major Change Confirmation</h4>
            <p className="warning-text">
              <strong>Warning:</strong> Changing legal numbers like the GSTIN can have serious tax and legal implications for all documents. Ensure these numbers are absolutely correct.
            </p>
            <p>This action cannot be easily undone.</p>
             <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowConfirmation(false)}>Back to Edit</button>
              <button className="btn btn-danger" onClick={handleSaveChanges} disabled={isSaving}>
                {isSaving ? "Saving..." : "I Understand, Confirm Update"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* --- Edit Bank Details Modal --- */}
      <Modal isOpen={activeModal === 'bank'} onClose={handleCloseModal} title="Edit Bank Details">
        <div className="form-group">
          <label htmlFor="bank_details">Bank Details (HTML allowed for formatting)</label>
          <textarea id="bank_details" name="bank_details" rows="5" value={formData.bank_details || ''} onChange={handleInputChange}></textarea>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={handleCloseModal}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveChanges} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </Modal>

      <style>{`
        .settings-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 1rem;
            max-height: calc(100vh - 100px); /* Set a height limit */
            overflow-y: auto; /* Add scrollbar if content overflows */
            adding-right: 15px; /* Optional: prevents scrollbar from overlapping content */
        }
        .settings-header {
          margin-bottom: 1.5rem;
          color: #333;
        }
        .settings-card {
          background-color: #fff;
          border-radius: 10px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          margin-bottom: 2rem;
          overflow: hidden;
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }
        .card-header h4 {
          margin: 0;
          font-size: 1.1rem;
          color: #111827;
        }
        .card-body {
          padding: 1.5rem;
        }
        .field-row {
          display: flex;
          flex-wrap: wrap;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f3f4f6;
        }
        .field-row:last-child {
          border-bottom: none;
        }
        .field-label {
          flex-basis: 200px;
          font-weight: 500;
          color: #4b5563;
        }
        .field-value {
          flex-grow: 1;
          color: #1f2937;
        }
        .field-value i.not-set {
          color: #9ca3af;
        }
        .btn {
          padding: 0.35rem 0.85rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-outline {
          background: transparent;
          color: #666;
          border: 1px solid #ccc;
        }
        .btn-outline:hover {
          background: #f5f5f5;
        }
        .btn-primary {
          background: var(--theme-color);
          color: #fff;
          border: none;
        }
        .btn-danger {
          background-color: #dc3545;
          color: white;
          border: none;
        }

        /* Modal Styles */
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background-color: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .modal-header h3 { margin: 0; }
        .modal-close-btn {
          background: none;
          border: none;
          font-size: 1.75rem;
          cursor: pointer;
          color: #6b7280;
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background-color: #f9fafb;
        }
        .form-group {
          margin-bottom: 1.25rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #374151;
        }
        .form-group input, .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
        }
        .confirmation-dialog h4 {
          color: #c81e1e;
        }
        .warning-text {
          background-color: #fffbeb;
          border-left: 4px solid #f59e0b;
          padding: 1rem;
          margin: 1rem 0;
          color: #92400e;
        }
      `}</style>
    </div>
  );
};

export default CompanySettings;
